import { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useUploadAvatar } from '../hooks/useUploadAvatar';
import { useDeleteAvatar } from '../hooks/useDeleteAvatar';
import type { MyProfileResponse, ApiError } from '../types/myProfile';

const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_MIME = ['image/png', 'image/jpeg', 'image/webp'];

interface AvatarTabProps {
  profile: MyProfileResponse;
  onSuccess?: (msg: string) => void;
}

function getInitials(displayName: string): string {
  return displayName
    .split(' ')
    .map((w) => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

export function AvatarTab({ profile, onSuccess }: AvatarTabProps) {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(profile.avatarDownloadUrl);
  const [dragOver, setDragOver] = useState(false);
  const [fileError, setFileError] = useState<string | undefined>();
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [serverError, setServerError] = useState<string | undefined>();

  const uploadMutation = useUploadAvatar();
  const deleteMutation = useDeleteAvatar();

  const validateFile = (file: File): string | null => {
    if (file.size > MAX_FILE_SIZE) {
      return t('myProfile.avatar.error.tooLarge');
    }
    if (!ALLOWED_MIME.includes(file.type)) {
      return t('myProfile.avatar.error.invalidType');
    }
    return null;
  };

  const handleFileUpload = useCallback(
    (file: File) => {
      setFileError(undefined);
      setServerError(undefined);

      const validationError = validateFile(file);
      if (validationError) {
        setFileError(validationError);
        return;
      }

      // Client-side preview
      const objectUrl = URL.createObjectURL(file);
      setPreviewUrl(objectUrl);

      uploadMutation.mutate(file, {
        onSuccess: (data) => {
          setPreviewUrl(data.downloadUrl);
          URL.revokeObjectURL(objectUrl);
          onSuccess?.(t('myProfile.avatar.toast.uploaded'));
        },
        onError: (err: unknown) => {
          const apiErr = err as ApiError;
          setPreviewUrl(profile.avatarDownloadUrl);
          URL.revokeObjectURL(objectUrl);
          if (apiErr.code === 'PROFILE-413-001') {
            setServerError(t('myProfile.error.avatarTooLarge'));
          } else if (apiErr.code === 'PROFILE-415-001') {
            setServerError(t('myProfile.error.avatarMimeNotAllowed'));
          } else if (apiErr.code?.startsWith('HTTP-423') || (apiErr as { status?: number }).status === 423) {
            // ROLE_EXEC sandbox — 발생하지 않아야 하나 방어적 처리
            setServerError(t('myProfile.avatar.error.sandboxLocked'));
          } else {
            setServerError(apiErr.message ?? t('myProfile.avatar.error.uploadFailed'));
          }
        },
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [uploadMutation, profile.avatarDownloadUrl, onSuccess]
  );

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileUpload(file);
    // Reset input so same file can be re-selected
    e.target.value = '';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFileUpload(file);
  };

  const handleDelete = () => {
    setServerError(undefined);
    deleteMutation.mutate(undefined, {
      onSuccess: () => {
        setPreviewUrl(null);
        setShowDeleteConfirm(false);
        onSuccess?.(t('myProfile.avatar.toast.removed'));
      },
      onError: (err: unknown) => {
        const apiErr = err as ApiError;
        setShowDeleteConfirm(false);
        if (apiErr.code === 'PROFILE-404-001') {
          setServerError(t('myProfile.avatar.error.noAvatar'));
        } else if (apiErr.code?.startsWith('HTTP-423') || (apiErr as { status?: number }).status === 423) {
          // ROLE_EXEC sandbox — 발생하지 않아야 하나 방어적 처리
          setServerError(t('myProfile.avatar.error.sandboxLocked'));
        } else {
          setServerError(apiErr.message ?? t('myProfile.avatar.error.deleteFailed'));
        }
      },
    });
  };

  const btnPrimary: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 'var(--spacing-2)',
    padding: 'var(--spacing-2) var(--spacing-4)',
    background: 'var(--color-brand-primary)',
    color: 'var(--color-text-inverse)',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'var(--font-family-sans)',
  };

  const btnDanger: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 'var(--spacing-2)',
    padding: 'var(--spacing-2) var(--spacing-4)',
    background: 'var(--color-danger)',
    color: 'var(--color-text-inverse)',
    border: 'none',
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'var(--font-family-sans)',
  };

  const btnGhost: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 'var(--spacing-2)',
    padding: 'var(--spacing-2) var(--spacing-4)',
    background: 'transparent',
    color: 'var(--color-text-secondary)',
    border: '1px solid var(--color-surface-border)',
    borderRadius: 'var(--radius-md)',
    fontSize: 'var(--font-size-sm)',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'var(--font-family-sans)',
  };

  const initials = getInitials(profile.displayName);
  const isBusy = uploadMutation.isPending || deleteMutation.isPending;

  return (
    <div style={{ maxWidth: 520 }}>
      <h2
        style={{
          margin: 0,
          marginBottom: 'var(--spacing-6)',
          fontSize: 'var(--font-size-lg)',
          fontWeight: 700,
          color: 'var(--color-text-primary)',
        }}
      >
        {t('myProfile.avatar.title')}
      </h2>

      {(fileError || serverError) && (
        <div
          role="alert"
          aria-live="polite"
          style={{
            padding: 'var(--spacing-3) var(--spacing-4)',
            marginBottom: 'var(--spacing-4)',
            background: 'color-mix(in srgb, var(--color-danger) 10%, var(--color-surface-muted))',
            border: '1px solid var(--color-danger)',
            borderRadius: 'var(--radius-md)',
            fontSize: 'var(--font-size-sm)',
            color: 'var(--color-danger)',
          }}
        >
          {fileError ?? serverError}
        </div>
      )}

      {/* Preview + dropzone */}
      <div style={{ display: 'flex', gap: 'var(--spacing-6)', alignItems: 'flex-start', flexWrap: 'wrap' }}>
        {/* Current avatar */}
        <div style={{ flexShrink: 0 }}>
          <div
            style={{
              width: 120,
              height: 120,
              borderRadius: 'var(--radius-full)',
              background: previewUrl
                ? 'var(--color-surface-elevated)'
                : 'linear-gradient(135deg, var(--color-brand-primary), var(--color-brand-secondary))',
              color: 'var(--color-text-inverse)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 48,
              fontWeight: 800,
              border: '3px solid color-mix(in srgb, var(--color-brand-primary) 50%, var(--color-surface-elevated))',
              overflow: 'hidden',
              position: 'relative',
            }}
            aria-label={previewUrl ? t('myProfile.avatar.preview') : t('myProfile.avatar.noAvatar')}
          >
            {isBusy && (
              <div
                style={{
                  position: 'absolute',
                  inset: 0,
                  background: 'var(--color-overlay-backdrop)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 'var(--radius-full)',
                }}
              >
                <span
                  style={{
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-text-inverse)',
                    fontWeight: 600,
                  }}
                >
                  {uploadMutation.isPending ? t('myProfile.avatar.uploadingShort') : t('myProfile.avatar.removingShort')}
                </span>
              </div>
            )}
            {previewUrl ? (
              <img
                src={previewUrl}
                alt={t('myProfile.avatar.preview')}
                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
              />
            ) : (
              initials
            )}
          </div>
          {!previewUrl && (
            <p
              style={{
                textAlign: 'center',
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-text-muted)',
                marginTop: 'var(--spacing-1)',
              }}
            >
              {t('myProfile.avatar.defaultAvatar')}
            </p>
          )}
        </div>

        {/* Drag & drop upload zone */}
        <div style={{ flex: 1, minWidth: 200 }}>
          <div
            role="button"
            aria-label={t('myProfile.avatar.dropzoneAriaLabel')}
            tabIndex={0}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => !isBusy && fileInputRef.current?.click()}
            onKeyDown={(e) => {
              if ((e.key === 'Enter' || e.key === ' ') && !isBusy) {
                fileInputRef.current?.click();
              }
            }}
            style={{
              padding: 'var(--spacing-6)',
              border: `2px dashed ${dragOver ? 'var(--color-brand-primary)' : 'var(--color-surface-border)'}`,
              borderRadius: 'var(--radius-lg)',
              background: dragOver
                ? 'color-mix(in srgb, var(--color-brand-primary) 8%, var(--color-surface-muted))'
                : 'var(--color-surface-muted)',
              textAlign: 'center',
              cursor: isBusy ? 'not-allowed' : 'pointer',
              transition: 'border-color 0.2s, background 0.2s',
              opacity: isBusy ? 0.6 : 1,
            }}
          >
            <div style={{ fontSize: 32, marginBottom: 'var(--spacing-2)', lineHeight: 1 }}>
              {uploadMutation.isPending ? '⟳' : '↑'}
            </div>
            <p style={{ margin: 0, fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)', fontWeight: 500 }}>
              {uploadMutation.isPending
                ? t('myProfile.avatar.uploading')
                : t('myProfile.avatar.dropzone')}
            </p>
            <p style={{ margin: 'var(--spacing-1) 0 0', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
              {t('myProfile.avatar.restriction')}
            </p>
          </div>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            onChange={handleInputChange}
            style={{ display: 'none' }}
            aria-hidden="true"
            tabIndex={-1}
          />

          {/* Action buttons */}
          <div style={{ display: 'flex', gap: 'var(--spacing-2)', marginTop: 'var(--spacing-4)', flexWrap: 'wrap' }}>
            <button
              type="button"
              onClick={() => !isBusy && fileInputRef.current?.click()}
              disabled={isBusy}
              style={{
                ...btnPrimary,
                opacity: isBusy ? 0.6 : 1,
                cursor: isBusy ? 'not-allowed' : 'pointer',
              }}
              aria-label={t('myProfile.avatar.uploadButton')}
            >
              {uploadMutation.isPending ? t('myProfile.avatar.uploading') : t('myProfile.avatar.uploadButton')}
            </button>
            {previewUrl && (
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={isBusy}
                style={{
                  ...btnGhost,
                  color: 'var(--color-danger)',
                  borderColor: 'var(--color-danger)',
                  opacity: isBusy ? 0.6 : 1,
                  cursor: isBusy ? 'not-allowed' : 'pointer',
                }}
                aria-label={t('myProfile.avatar.removeButton')}
              >
                {deleteMutation.isPending ? t('myProfile.avatar.removing') : t('myProfile.avatar.removeButton')}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Delete confirmation dialog */}
      {showDeleteConfirm && (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="delete-dialog-title"
          aria-describedby="delete-dialog-desc"
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 500,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'var(--color-overlay-backdrop)',
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setShowDeleteConfirm(false);
          }}
        >
          <div
            style={{
              background: 'var(--color-surface-background)',
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-lg)',
              padding: 'var(--spacing-6)',
              maxWidth: 400,
              width: '90vw',
              boxShadow: 'var(--shadow-lg)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3
              id="delete-dialog-title"
              style={{ margin: 0, marginBottom: 'var(--spacing-2)', fontSize: 'var(--font-size-base)', fontWeight: 700 }}
            >
              {t('myProfile.avatar.dialog.title')}
            </h3>
            <p
              id="delete-dialog-desc"
              style={{ margin: 0, marginBottom: 'var(--spacing-6)', color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)' }}
            >
              {t('myProfile.avatar.dialog.description')}
            </p>
            <div style={{ display: 'flex', gap: 'var(--spacing-2)', justifyContent: 'flex-end' }}>
              <button
                type="button"
                autoFocus
                onClick={() => setShowDeleteConfirm(false)}
                style={btnGhost}
              >
                {t('myProfile.avatar.dialog.cancel')}
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={deleteMutation.isPending}
                style={{
                  ...btnDanger,
                  opacity: deleteMutation.isPending ? 0.6 : 1,
                  cursor: deleteMutation.isPending ? 'not-allowed' : 'pointer',
                }}
              >
                {deleteMutation.isPending ? t('myProfile.avatar.removing') : t('myProfile.avatar.dialog.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
